/*
 * nb-cart.js
 *
 * 1. Intercepts ATC form submits, POSTs to /cart/add.js, opens the drawer.
 * 2. Replaces the cart icon click in the header with "open drawer" (instead
 *    of navigating to /cart).
 * 3. Renders cart line items, totals, qty controls, remove buttons by
 *    calling /cart.js.
 * 4. Keeps the header cart count badge in sync on every cart mutation.
 */
(function () {
  const drawer = document.querySelector('[data-nb-cart-drawer]');
  if (!drawer) return;

  const body = drawer.querySelector('[data-nb-cart-body]');
  const footer = drawer.querySelector('[data-nb-cart-footer]');
  const subtotalEl = drawer.querySelector('[data-nb-cart-subtotal]');
  const countPill = drawer.querySelector('[data-nb-cart-count-pill]');
  const closeBtns = drawer.querySelectorAll('[data-nb-cart-close]');

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2);
  }
  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  let lastFocused = null;

  function openDrawer() {
    lastFocused = document.activeElement;
    drawer.setAttribute('data-nb-open', '');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    const closeBtn = drawer.querySelector('.nb-cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }
  function closeDrawer() {
    drawer.removeAttribute('data-nb-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    // Restore focus to whatever opened the drawer (cart icon / ATC button).
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }
  closeBtns.forEach(function (b) {
    b.addEventListener('click', closeDrawer);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.hasAttribute('data-nb-open')) closeDrawer();
  });
  // Focus trap — keep Tab/Shift+Tab inside the open drawer.
  drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !drawer.hasAttribute('data-nb-open')) return;
    const focusables = Array.prototype.filter.call(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  function updateHeaderCount(itemCount) {
    // Header cart badge — add/update/remove the .nb-header__cart-count span
    // inside each cart icon link. (A second pass over all badges was removed:
    // it re-wrote a "0" onto badges the loop below had just removed.)
    const cartLinks = document.querySelectorAll('.nb-header__util-btn--cart');
    cartLinks.forEach(function (link) {
      let badge = link.querySelector('.nb-header__cart-count');
      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'nb-header__cart-count';
          link.appendChild(badge);
        }
        badge.textContent = String(itemCount);
      } else if (badge) {
        badge.remove();
      }
    });
    // Drawer header pill
    if (countPill) {
      if (itemCount > 0) {
        countPill.textContent = String(itemCount);
        countPill.hidden = false;
      } else {
        countPill.hidden = true;
      }
    }
  }

  function renderLine(item) {
    const img = item.image
      ? '<div class="nb-cart-drawer__line-img"><img src="' + escapeHtml(item.image) + '" alt=""></div>'
      : '<div class="nb-cart-drawer__line-img"></div>';
    const variant =
      item.variant_title && item.variant_title !== 'Default Title'
        ? '<div class="nb-cart-drawer__line-variant">' + escapeHtml(item.variant_title) + '</div>'
        : '';
    // Visible line item properties (e.g. "Player name: ..." on event tickets).
    // Keys starting with "_" are hidden by Shopify convention.
    const properties = item.properties || {};
    const props = Object.keys(properties)
      .filter(function (key) {
        const value = properties[key];
        return key.charAt(0) !== '_' && value != null && String(value).trim() !== '';
      })
      .map(function (key) {
        return (
          '<div class="nb-cart-drawer__line-prop">' +
          escapeHtml(key) +
          ': ' +
          escapeHtml(properties[key]) +
          '</div>'
        );
      })
      .join('');
    return (
      '<div class="nb-cart-drawer__line" data-key="' + escapeHtml(item.key) + '">' +
      img +
      '<div class="nb-cart-drawer__line-info">' +
      '<a href="' + escapeHtml(item.url) + '" class="nb-cart-drawer__line-title">' +
      escapeHtml(item.product_title) +
      '</a>' +
      variant +
      props +
      '<div class="nb-cart-drawer__line-controls">' +
      '<div class="nb-cart-drawer__line-qty">' +
      '<button type="button" data-nb-qty data-delta="-1" aria-label="Decrease">−</button>' +
      '<span>' + item.quantity + '</span>' +
      '<button type="button" data-nb-qty data-delta="1" aria-label="Increase">+</button>' +
      '</div>' +
      '<button type="button" class="nb-cart-drawer__line-remove" data-nb-remove>Remove</button>' +
      '</div>' +
      '</div>' +
      '<div class="nb-cart-drawer__line-price">' + money(item.final_line_price) + '</div>' +
      '</div>'
    );
  }

  function render(cart) {
    updateHeaderCount(cart.item_count);

    if (cart.item_count === 0) {
      body.innerHTML =
        '<div class="nb-cart-drawer__empty">' +
        '<p>Your cart\'s empty.</p>' +
        '<a href="/collections/all" class="nb-btn nb-btn--primary">Start shopping</a>' +
        '</div>';
      footer.hidden = true;
      return;
    }

    body.innerHTML = cart.items.map(renderLine).join('');
    footer.hidden = false;
    if (subtotalEl) subtotalEl.textContent = money(cart.items_subtotal_price);

    // Wire qty + remove buttons
    body.querySelectorAll('[data-nb-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const line = btn.closest('[data-key]');
        if (!line) return;
        const key = line.getAttribute('data-key');
        const delta = parseInt(btn.getAttribute('data-delta'), 10);
        const qtySpan = line.querySelector('.nb-cart-drawer__line-qty span');
        const current = parseInt(qtySpan ? qtySpan.textContent : '1', 10);
        updateQuantity(key, Math.max(0, current + delta));
      });
    });
    body.querySelectorAll('[data-nb-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const line = btn.closest('[data-key]');
        if (!line) return;
        updateQuantity(line.getAttribute('data-key'), 0);
      });
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart fetch failed');
        return r.json();
      })
      .then(function (cart) {
        render(cart);
        return cart;
      });
  }

  function updateQuantity(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart change failed');
        return r.json();
      })
      .then(function (cart) {
        render(cart);
      })
      .catch(function () {
        // Re-sync from source of truth so the UI doesn't show a stale quantity.
        fetchCart();
      });
  }

  // Intercept ATC form submits
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-nb-product-form]')) return;
    e.preventDefault();
    if (form.dataset.nbAdding) return; // guard against double-submit (Enter / rapid clicks)
    form.dataset.nbAdding = '1';
    // Line item properties (e.g. Player name / Player ID):
    //  - a required field that is blank after trimming blocks the add. Native
    //    `required` accepts whitespace-only input, and silently dropping it
    //    would add a ticket with no player identity.
    //  - values are trimmed, and blank optional ones are dropped so they don't
    //    land on the order as a bare "Key:" line.
    const propertyInputs = Array.from(form.querySelectorAll('[name^="properties["]'));
    for (let i = 0; i < propertyInputs.length; i++) {
      const input = propertyInputs[i];
      if (input.required && String(input.value || '').trim() === '') {
        input.value = ''; // so the browser's own "required" message shows
        input.reportValidity();
        delete form.dataset.nbAdding;
        return;
      }
    }
    const formData = new FormData(form);
    Array.from(formData.keys()).forEach(function (key) {
      if (key.indexOf('properties[') !== 0) return;
      const trimmed = String(formData.get(key)).trim();
      if (trimmed === '') {
        formData.delete(key);
      } else {
        formData.set(key, trimmed);
      }
    });
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        return fetchCart();
      })
      .then(function () {
        openDrawer();
      })
      .catch(function () {
        // Fallback: submit the form normally so the user still ends up at /cart
        form.removeAttribute('data-nb-product-form');
        form.submit();
      })
      .finally(function () {
        delete form.dataset.nbAdding;
        if (submitBtn) {
          submitBtn.disabled = false;
          if (originalText != null) submitBtn.textContent = originalText;
        }
      });
  });

  // Intercept cart icon click in the header → open drawer instead of navigating.
  // If the cart fetch fails, fall back to navigating to the cart page so the
  // click isn't a dead no-op (preventDefault already ran).
  document.querySelectorAll('.nb-header__util-btn--cart').forEach(function (link) {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      fetchCart()
        .then(openDrawer)
        .catch(function () {
          window.location.href = link.href;
        });
    });
  });

  // Pre-fetch cart on load so the drawer is responsive when first opened
  fetchCart();
})();
